export const useImageCompressor = {
  /**
   * Сжимает изображение до заданного максимального размера
   * @param {File} file - Исходный файл изображения
   * @param {number} maxSize - Максимальный размер стороны в пикселях
   * @param {number} [quality=0.8] - Качество сжатия (0-1)
   * @param {string} [outputType='image/jpeg'] - Тип выходного изображения
   * @returns {Promise<File>} - Сжатый файл изображения
   */
  async compressImage(
    file: File,
    maxSize: number,
    quality = 0.8,
    outputType = 'image/jpeg',
  ): Promise<File> {
    try {
      const image = await this.loadImage(file)

      const { width, height } = this.calculateNewSize(image.width, image.height, maxSize)

      const canvas = this.createCanvas(image, width, height)
      const blob = await this.canvasToBlob(canvas, outputType, quality)

      return this.createFile(blob, file.name, outputType)
    } catch (error) {
      console.error('Ошибка сжатия изображения:', error)
      throw error
    }
  },

  /**
   * Загрузка изображения из File
   * @private
   */
  loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = URL.createObjectURL(file)
    })
  },

  /**
   * Вычисление новых размеров изображения
   * @private
   */
  calculateNewSize(originalWidth: number, originalHeight: number, maxSize: number) {
    let width = originalWidth
    let height = originalHeight

    if (width > maxSize || height > maxSize) {
      if (width > height) {
        height = Math.round((height * maxSize) / width)
        width = maxSize
      } else {
        width = Math.round((width * maxSize) / height)
        height = maxSize
      }
    }

    return { width, height }
  },

  /**
   * Создание canvas с новыми размерами
   * @private
   */
  createCanvas(image: HTMLImageElement, width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('Не удалось получить контекст canvas')
    }

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(image, 0, 0, width, height)

    return canvas
  },

  /**
   * Конвертация canvas в blob
   * @private
   */
  canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Не удалось создать blob из canvas'))
          }
        },
        type,
        quality,
      )
    })
  },

  /**
   * Создание File из blob
   * @private
   */
  createFile(blob: Blob, originalName: string, type: string): File {
    const extension = type.split('/')[1]
    const name = originalName.replace(/\.[^/.]+$/, '') + `.${extension}`

    return new File([blob], name, {
      type: type,
      lastModified: Date.now(),
    })
  },
}
